/* ═══════════════════════════════════════════════════════════════
   TeamTree — Directory's nested team tree (Team Management UX
   Overhaul, item 2). Renders every team with its parentTeamId
   nesting. Click a team -> opens its editor panel (TeamEditorPanel).
   Drag-and-drop, click-primary per docs/DESIGN.md §10.2 (list/
   assignment UIs are click-to-select + panel-assign primary, drag
   optional):
     - Drag a Directory person row onto a team -> adds them (existing
       team-assignment API, no session model).
     - Drag a team onto another team -> reparents (nest), guarded by
       the same cycle-safety check TeamsPanel used for its parent
       picker (never allow a team to become its own descendant).
     - Drag a team onto the tree's empty/root area -> un-nests it.
   Drop targets highlight on hover; invalid targets (self/descendant)
   visibly dim and reject the drop.
   ═══════════════════════════════════════════════════════════════ */

import { useState, type DragEvent } from 'react';
import { ChevronRight, ChevronDown, Users } from 'lucide-react';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';
import type { Team, TeamAssignment } from '../../../shared/types';
import { emptyPill } from './styles';

export const TEAM_DRAG_TYPE = 'application/x-sf-team';
export const MEMBER_DRAG_TYPE = 'application/x-sf-member';

export interface TeamTreeProps {
  teams: Team[];
  teamAssignments: TeamAssignment[];
  selectedTeamId?: string | null;
  canEdit: boolean;
  onSelectTeam: (t: Team) => void;
  onDropMemberOnTeam: (memberId: string, teamId: string) => void;
  onReparentTeam: (teamId: string, newParentId: string | undefined) => void;
}

function getDescendantIds(teams: Team[], id: string): Set<string> {
  const desc = new Set<string>();
  const queue = teams.filter((t) => t.parentTeamId === id);
  while (queue.length) {
    const t = queue.pop()!;
    desc.add(t.id);
    queue.push(...teams.filter((c) => c.parentTeamId === t.id));
  }
  return desc;
}

export function TeamTree({
  teams,
  teamAssignments,
  selectedTeamId,
  canEdit,
  onSelectTeam,
  onDropMemberOnTeam,
  onReparentTeam,
}: TeamTreeProps) {
  const [dragOverTeamId, setDragOverTeamId] = useState<string | null>(null);
  const [dragOverRoot, setDragOverRoot] = useState(false);
  const [draggingTeamId, setDraggingTeamId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const byParent = new Map<string, Team[]>();
  const ROOT = '__root__';
  // Orphan defense: a team whose parentTeamId doesn't resolve to another
  // team IN THIS LIST (server should now promote children on delete, but a
  // team can also still be mid-flight from a stale cache, or reference a
  // team hidden by some other filter) renders as a root-level entry rather
  // than silently vanishing — a dangling parent id must never make a whole
  // subtree disappear from the Directory (2026-08-19 bug: parent team
  // deleted, live children kept the deleted id and dropped out of this
  // tree entirely while still showing on the org chart).
  const knownTeamIds = new Set(teams.map((t) => t.id));
  for (const t of teams) {
    const key = t.parentTeamId && knownTeamIds.has(t.parentTeamId) ? t.parentTeamId : ROOT;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key)!.push(t);
  }
  for (const list of byParent.values()) list.sort((a, b) => a.name.localeCompare(b.name));

  function isValidDropTarget(targetId: string): boolean {
    if (!draggingTeamId) return true; // a member drag is always a valid drop onto any team
    if (draggingTeamId === targetId) return false;
    return !getDescendantIds(teams, draggingTeamId).has(targetId);
  }

  function clearDragState() {
    setDraggingTeamId(null);
    setDragOverTeamId(null);
    setDragOverRoot(false);
  }

  function handleDragOver(e: DragEvent, teamId: string) {
    if (!canEdit) return;
    const types = e.dataTransfer.types;
    if (!types.includes(MEMBER_DRAG_TYPE) && !types.includes(TEAM_DRAG_TYPE)) return;
    e.preventDefault();
    // A tree-item drop target sits INSIDE the root drop zone (un-nest) —
    // without stopping propagation, the same dragover/drop bubbles up to
    // the root container and its handler fires too, so a drop meant to
    // nest onto this item would ALSO immediately un-nest via the root's
    // handler right behind it (found via the functional smoke test:
    // "nested under X" toast immediately followed by "un-nested").
    e.stopPropagation();
    if (dragOverTeamId !== teamId) setDragOverTeamId(teamId);
  }

  function handleDrop(e: DragEvent, teamId: string) {
    e.preventDefault();
    e.stopPropagation();
    setDragOverTeamId(null);
    if (!canEdit) return;
    const memberId = e.dataTransfer.getData(MEMBER_DRAG_TYPE);
    if (memberId) {
      onDropMemberOnTeam(memberId, teamId);
      return;
    }
    const draggedTeamId = e.dataTransfer.getData(TEAM_DRAG_TYPE);
    if (draggedTeamId && isValidDropTarget(teamId)) {
      onReparentTeam(draggedTeamId, teamId);
    }
  }

  function renderNode(t: Team, depth: number) {
    const children = byParent.get(t.id) || [];
    const assigns = teamAssignments.filter((a) => a.teamId === t.id);
    const active = selectedTeamId === t.id;
    const isDragOver = dragOverTeamId === t.id;
    const invalidTarget = draggingTeamId != null && !isValidDropTarget(t.id);
    const isCollapsed = collapsed.has(t.id);

    return (
      <div key={t.id}>
        <div
          draggable={canEdit}
          onDragStart={(e) => {
            e.dataTransfer.setData(TEAM_DRAG_TYPE, t.id);
            e.dataTransfer.effectAllowed = 'move';
            setDraggingTeamId(t.id);
          }}
          onDragEnd={clearDragState}
          onDragOver={(e) => handleDragOver(e, t.id)}
          onDragLeave={() => setDragOverTeamId((cur) => (cur === t.id ? null : cur))}
          onDrop={(e) => handleDrop(e, t.id)}
          onClick={() => onSelectTeam(t)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelectTeam(t);
            }
          }}
          role="treeitem"
          tabIndex={0}
          aria-selected={active}
          title={t.name}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: rem(6),
            padding: `${rem(6)} var(--space-2)`,
            marginLeft: depth * 14,
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            border:
              isDragOver && !invalidTarget
                ? '1px dashed var(--ac)'
                : active
                  ? '1px solid var(--ac)'
                  : '1px solid transparent',
            background:
              isDragOver && !invalidTarget
                ? 'color-mix(in srgb, var(--ac) 12%, transparent)'
                : active
                  ? 'color-mix(in srgb, var(--ac) 8%, transparent)'
                  : 'transparent',
            opacity: invalidTarget ? 0.4 : 1,
            transition: 'background .1s, opacity .1s',
          }}
        >
          {children.length > 0 ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setCollapsed((prev) => {
                  const next = new Set(prev);
                  if (next.has(t.id)) next.delete(t.id);
                  else next.add(t.id);
                  return next;
                });
              }}
              aria-label={isCollapsed ? `Expand ${t.name}` : `Collapse ${t.name}`}
              style={{
                display: 'flex',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--tm)',
                padding: rem(2),
                flexShrink: 0,
              }}
            >
              {isCollapsed ? (
                <Icon icon={ChevronRight} em={0.9231} aria-hidden="true" />
              ) : (
                <Icon icon={ChevronDown} em={0.9231} aria-hidden="true" />
              )}
            </button>
          ) : (
            <span style={{ width: 'var(--space-4)', flexShrink: 0 }} />
          )}
          <span
            aria-hidden="true"
            style={{
              width: rem(9),
              height: rem(9),
              borderRadius: rem(3),
              background: t.color || 'var(--tm)',
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: 'calc(var(--font-size-base) * 12.5 / 13)',
              fontWeight: 600,
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {t.name}
          </span>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--tm)', flexShrink: 0 }}>
            {assigns.length}
          </span>
        </div>
        {!isCollapsed && children.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  }

  const roots = byParent.get(ROOT) || [];

  return (
    <div
      data-team-tree-root
      style={{
        width: rem(220),
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid var(--bd)',
        paddingRight: 'var(--space-2)',
        marginRight: 'var(--space-2)',
        overflow: 'auto',
      }}
      onDragOver={(e) => {
        if (!canEdit || !draggingTeamId) return;
        e.preventDefault();
        if (!dragOverRoot) setDragOverRoot(true);
      }}
      onDragLeave={() => setDragOverRoot(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOverRoot(false);
        if (!canEdit) return;
        const draggedTeamId = e.dataTransfer.getData(TEAM_DRAG_TYPE);
        if (draggedTeamId) onReparentTeam(draggedTeamId, undefined);
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 700,
          color: 'var(--tm)',
          textTransform: 'uppercase',
          letterSpacing: '.05em',
          padding: 'var(--space-1) var(--space-2) var(--space-2)',
        }}
      >
        Teams
      </div>
      {teams.length === 0 ? (
        <div style={emptyPill}>
          <Icon icon={Users} em={1.5385} className="mx-auto mb-2" aria-hidden="true" />
          No teams yet.
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: rem(2),
            flex: 1,
            border: dragOverRoot ? '1px dashed var(--ac)' : '1px solid transparent',
            borderRadius: 'var(--radius-md)',
            padding: rem(2),
          }}
        >
          {roots.map((t) => renderNode(t, 0))}
        </div>
      )}
      {draggingTeamId && (
        <div
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--tm)',
            padding: `${rem(6)} var(--space-2)`,
            fontStyle: 'italic',
          }}
        >
          Drop here to un-nest from its parent
        </div>
      )}
    </div>
  );
}
